import { supabase } from '@/integrations/supabase/client';

export interface ABExperiment {
  id: string;
  name: string;
  description?: string;
  status: string;
  start_date?: string;
  end_date?: string;
  target_audience: any;
  traffic_percentage: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ABVariant {
  id: string;
  experiment_id: string;
  name: string;
  description?: string;
  config: any;
  traffic_weight: number;
  is_control: boolean;
  created_at: string;
}

export interface ABParticipant {
  id: string;
  experiment_id: string;
  variant_id: string;
  user_id?: string;
  session_id: string;
  assigned_at: string;
}

export interface ABConversion {
  id: string;
  experiment_id: string;
  variant_id: string;
  participant_id: string;
  event_type: string;
  event_value?: number;
  metadata: any;
  occurred_at: string;
}

export interface ExperimentResult {
  experiment: ABExperiment;
  variants: Array<{
    variant: ABVariant;
    participants: number;
    conversions: number;
    conversion_rate: number;
    confidence_interval?: [number, number];
  }>;
  total_participants: number;
  statistical_significance: boolean;
}

export class ABTestingService {
  
  /**
   * Get all active experiments
   */
  static async getActiveExperiments(): Promise<ABExperiment[]> {
    try {
      const { data, error } = await supabase
        .from('ab_experiments')
        .select('*')
        .eq('status', 'active')
        .lte('start_date', new Date().toISOString())
        .or(`end_date.is.null,end_date.gte.${new Date().toISOString()}`);

      if (error) {
        console.error('Error fetching active experiments:', error);
        return [];
      }

      return (data || []) as ABExperiment[];
    } catch (error) {
      console.error('Error in getActiveExperiments:', error);
      return [];
    }
  }

  /**
   * Get variants for an experiment
   */
  static async getExperimentVariants(experimentId: string): Promise<ABVariant[]> {
    try {
      const { data, error } = await supabase
        .from('ab_variants')
        .select('*')
        .eq('experiment_id', experimentId)
        .order('is_control', { ascending: false });

      if (error) {
        console.error('Error fetching variants:', error);
        return [];
      }

      return (data || []) as ABVariant[];
    } catch (error) {
      console.error('Error in getExperimentVariants:', error);
      return [];
    }
  }

  /**
   * Assign user/session to experiment variant
   */
  static async assignToVariant(
    experimentId: string,
    sessionId: string,
    userId?: string
  ): Promise<ABVariant | null> {
    try {
      // Check if already assigned
      const { data: existingAssignment } = await supabase
        .from('ab_participants')
        .select('variant_id, ab_variants(*)')
        .eq('experiment_id', experimentId)
        .eq('session_id', sessionId)
        .single();

      if (existingAssignment) {
        return existingAssignment.ab_variants as ABVariant;
      }

      // Get experiment variants
      const variants = await this.getExperimentVariants(experimentId);
      if (variants.length === 0) return null;

      // Calculate variant assignment based on traffic weights
      const totalWeight = variants.reduce((sum, v) => sum + v.traffic_weight, 0);
      const random = Math.random() * totalWeight;
      
      let currentWeight = 0;
      let selectedVariant = variants[0]; // fallback
      
      for (const variant of variants) {
        currentWeight += variant.traffic_weight;
        if (random <= currentWeight) {
          selectedVariant = variant;
          break;
        }
      }

      // Create assignment
      const { data: participantData, error: assignError } = await supabase
        .from('ab_participants')
        .insert({
          experiment_id: experimentId,
          variant_id: selectedVariant.id,
          user_id: userId || null,
          session_id: sessionId
        })
        .select()
        .single();

      if (assignError) {
        console.error('Error creating assignment:', assignError);
        return null;
      }

      return selectedVariant;
    } catch (error) {
      console.error('Error in assignToVariant:', error);
      return null;
    }
  }

  /**
   * Get user's assignment for an experiment
   */
  static async getUserAssignment(
    experimentId: string,
    sessionId: string
  ): Promise<ABVariant | null> {
    try {
      const { data, error } = await supabase
        .from('ab_participants')
        .select('variant_id, ab_variants(*)')
        .eq('experiment_id', experimentId)
        .eq('session_id', sessionId)
        .single();

      if (error || !data) {
        return null;
      }

      return data.ab_variants as ABVariant;
    } catch (error) {
      console.error('Error in getUserAssignment:', error);
      return null;
    }
  }

  /**
   * Record conversion event
   */
  static async recordConversion(
    experimentId: string,
    sessionId: string,
    eventType: string,
    eventValue?: number,
    metadata?: any
  ): Promise<boolean> {
    try {
      // Get participant assignment
      const { data: participant, error: participantError } = await supabase
        .from('ab_participants')
        .select('id, variant_id')
        .eq('experiment_id', experimentId)
        .eq('session_id', sessionId)
        .single();

      if (participantError || !participant) {
        console.error('Participant not found for conversion:', participantError);
        return false;
      }

      // Record conversion
      const { error: conversionError } = await supabase
        .from('ab_conversions')
        .insert({
          experiment_id: experimentId,
          variant_id: participant.variant_id,
          participant_id: participant.id,
          event_type: eventType,
          event_value: eventValue || null,
          metadata: metadata || {}
        });

      if (conversionError) {
        console.error('Error recording conversion:', conversionError);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in recordConversion:', error);
      return false;
    }
  }

  /**
   * Get experiment results and analytics
   */
  static async getExperimentResults(experimentId: string): Promise<ExperimentResult | null> {
    try {
      // Get experiment details
      const { data: experiment, error: expError } = await supabase
        .from('ab_experiments')
        .select('*')
        .eq('id', experimentId)
        .single();

      if (expError || !experiment) return null;

      // Get variants with participant and conversion counts
      const { data: variantData, error: variantError } = await supabase
        .from('ab_variants')
        .select(`
          *,
          participant_count:ab_participants(count),
          conversion_count:ab_conversions(count)
        `)
        .eq('experiment_id', experimentId);

      if (variantError || !variantData) return null;

      // Calculate conversion rates and format results
      const variants = variantData.map(variant => {
        const participants = (variant as any).participant_count?.[0]?.count || 0;
        const conversions = (variant as any).conversion_count?.[0]?.count || 0;
        const conversion_rate = participants > 0 ? (conversions / participants) * 100 : 0;

        return {
          variant: {
            id: variant.id,
            experiment_id: variant.experiment_id,
            name: variant.name,
            description: variant.description,
            config: variant.config,
            traffic_weight: variant.traffic_weight,
            is_control: variant.is_control,
            created_at: variant.created_at
          } as ABVariant,
          participants,
          conversions,
          conversion_rate
        };
      });

      const total_participants = variants.reduce((sum, v) => sum + v.participants, 0);
      
      // Simple statistical significance check (requires proper implementation)
      const statistical_significance = total_participants > 100 && variants.length >= 2;

      return {
        experiment: experiment as ABExperiment,
        variants,
        total_participants,
        statistical_significance
      };
    } catch (error) {
      console.error('Error in getExperimentResults:', error);
      return null;
    }
  }

  /**
   * Create new experiment (admin only)
   */
  static async createExperiment(experiment: {
    name: string;
    description?: string;
    start_date?: string;
    end_date?: string;
    target_audience?: any;
    traffic_percentage?: number;
  }): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('ab_experiments')
        .insert({
          name: experiment.name,
          description: experiment.description,
          start_date: experiment.start_date,
          end_date: experiment.end_date,
          target_audience: experiment.target_audience || {},
          traffic_percentage: experiment.traffic_percentage || 100,
          status: 'draft'
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating experiment:', error);
        return null;
      }

      return data?.id || null;
    } catch (error) {
      console.error('Error in createExperiment:', error);
      return null;
    }
  }

  /**
   * Add variant to experiment
   */
  static async addVariant(variant: {
    experiment_id: string;
    name: string;
    description?: string;
    config: any;
    traffic_weight?: number;
    is_control?: boolean;
  }): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('ab_variants')
        .insert({
          experiment_id: variant.experiment_id,
          name: variant.name,
          description: variant.description,
          config: variant.config,
          traffic_weight: variant.traffic_weight || 50,
          is_control: variant.is_control || false
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error adding variant:', error);
        return null;
      }

      return data?.id || null;
    } catch (error) {
      console.error('Error in addVariant:', error);
      return null;
    }
  }

  /**
   * Update experiment status
   */
  static async updateExperimentStatus(
    experimentId: string, 
    status: 'draft' | 'active' | 'paused' | 'completed'
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('ab_experiments')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', experimentId);

      if (error) {
        console.error('Error updating experiment status:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateExperimentStatus:', error);
      return false;
    }
  }
}